import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, AlertTriangle, TrendingUp, Users, DollarSign, Activity,
  MessageSquare, Music, Zap, Star, BarChart3, Calendar, ExternalLink,
  Copy, CheckCircle, RefreshCw, ChevronDown, Download, Eye, MousePointerClick, Megaphone, Bell, Clock
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7D' },
  { id: '30d', label: '30D' },
  { id: '90d', label: '90D' },
];

const FEATURES = [
  { id: 'chat', label: 'Live Chat', color: '#10B981' },
  { id: 'music', label: 'Music', color: '#8B5CF6' },
  { id: 'room_actions', label: 'Room Actions', color: '#EF4444' },
  { id: 'spotlights', label: 'Spotlights', color: '#F59E0B' },
  { id: 'polls', label: 'Polls', color: '#3B82F6' },
  { id: 'subscriptions', label: 'Subscriptions', color: '#E91E8D' },
  { id: 'day_passes', label: 'Day Passes', color: '#06B6D4' },
];

// Icon map kept separate from FEATURES to avoid babel plugin cross-file tracing crash
const FEATURE_ICONS = {
  chat: MessageSquare, music: Music, room_actions: Zap,
  spotlights: Star, polls: BarChart3, subscriptions: Users, day_passes: Activity,
};

const centsToUSD = (c) => `$${(c / 100).toFixed(2)}`;

/* ── Engagement Alerts ── */
const SEVERITY_STYLES = {
  notable: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', label: 'Notable' },
  strong: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', label: 'Strong' },
  exceptional: { bg: 'bg-[#E91E8D]/10', text: 'text-[#E91E8D]', border: 'border-[#E91E8D]/20', label: 'Exceptional' },
};

const AlertRow = ({ alert }) => {
  const s = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.notable;
  const isRev = alert.kpi === 'Revenue';
  const fmt = (v) => isRev ? `$${(v / 100).toFixed(2)}` : Math.round(v).toLocaleString();
  return (
    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${s.bg} ${s.border}`} data-testid={`alert-${alert.kpi.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="text-white/70 text-xs font-semibold">{alert.kpi}</span>
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${s.bg} ${s.text} border ${s.border} uppercase`}>{s.label}</span>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-white/40">Now: <span className="text-white/60 font-semibold">{fmt(alert.current_value)}</span></span>
          <span className="text-white/25">Avg: {fmt(alert.baseline_avg)}</span>
        </div>
      </div>
      <span className={`text-sm font-bold ${s.text}`}>+{alert.pct_increase.toFixed(0)}%</span>
    </div>
  );
};

const AlertHistoryRow = ({ alert }) => {
  const s = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.notable;
  const isRev = alert.kpi === 'Revenue';
  const fmt = (v) => isRev ? `$${(v / 100).toFixed(2)}` : Math.round(v).toLocaleString();
  const ts = alert.detected_at ? new Date(alert.detected_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : alert.detection_date || '';
  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border border-white/[0.04] bg-white/[0.02]`} data-testid={`alert-history-row-${alert.dedup_key || ''}`}>
      <div className="flex-shrink-0 w-[70px]">
        <span className="text-white/25 text-[9px] font-mono">{ts}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white/60 text-[11px] font-semibold">{alert.kpi}</span>
          <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${s.bg} ${s.text} border ${s.border} uppercase`}>{s.label}</span>
        </div>
        <div className="flex items-center gap-2 text-[9px] mt-0.5">
          <span className="text-white/30">Val: <span className="text-white/50">{fmt(alert.current_value)}</span></span>
          <span className="text-white/15">Avg: {fmt(alert.baseline_avg)}</span>
        </div>
      </div>
      <span className={`text-xs font-bold ${s.text}`}>+{Number(alert.pct_increase).toFixed(0)}%</span>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-black/95 border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="text-white/50 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-semibold" style={{ color: p.color || '#E91E8D' }}>
          {isCurrency ? centsToUSD(p.value) : p.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

// ==================== SETUP WIZARD ====================
const SetupWizard = ({ token, onSetupComplete }) => {
  const [sql, setSql] = useState('');
  const [dashboardUrl, setDashboardUrl] = useState('');
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/admin/analytics/setup/sql`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        setSql(data.sql || '');
        setDashboardUrl(data.dashboard_url || '');
        setInstructions(data.instructions || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [token]);

  const copySQL = () => {
    navigator.clipboard.writeText(sql);
    setCopied(true);
    toast.success('SQL copied to clipboard');
    setTimeout(() => setCopied(false), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[#E91E8D]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-setup-wizard">
      <div className="glass-card p-6 border-amber-400/20">
        <div className="flex items-start gap-3 mb-5">
          <AlertTriangle size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-white font-bold text-sm mb-1">Analytics Tables Required</h3>
            <p className="text-white/50 text-xs leading-relaxed">
              The analytics tables haven't been created in your Supabase project yet.
              Run the SQL below in the Supabase Dashboard to enable the Analytics Portal.
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-5">
          {instructions.map((step, i) => (
            <p key={i} className="text-white/60 text-xs pl-4">{step}</p>
          ))}
        </div>

        {dashboardUrl && (
          <a
            href={dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#3ECF8E]/10 text-[#3ECF8E] text-xs font-semibold border border-[#3ECF8E]/20 hover:bg-[#3ECF8E]/20 transition-colors mb-5"
            data-testid="open-supabase-btn"
          >
            <ExternalLink size={14} />
            Open Supabase SQL Editor
          </a>
        )}

        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/30 text-[10px] font-bold tracking-widest uppercase">Migration SQL</span>
            <button
              onClick={copySQL}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 text-white/60 text-[10px] font-semibold hover:bg-white/10 transition-colors"
              data-testid="copy-sql-btn"
            >
              {copied ? <CheckCircle size={12} className="text-green-400" /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Copy SQL'}
            </button>
          </div>
          <pre className="bg-black/40 border border-white/5 rounded-lg p-4 text-[10px] text-white/50 font-mono overflow-x-auto max-h-64 overflow-y-auto leading-relaxed">
            {sql}
          </pre>
        </div>
      </div>

      <button
        onClick={onSetupComplete}
        className="btn-pink !text-xs"
        data-testid="verify-setup-btn"
      >
        <CheckCircle size={14} />
        Verify Setup
      </button>
    </div>
  );
};

// ==================== KPI CARD ====================
const KPICard = ({ icon: Icon, label, value, subtext, color = '#E91E8D', testId }) => (
  <div className="glass-card p-4 min-w-0" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={14} style={{ color }} />
      </div>
      <span className="text-white/40 text-[10px] font-bold tracking-wider uppercase truncate">{label}</span>
    </div>
    <p className="text-white font-bold text-lg leading-none mb-1" data-testid={`${testId}-value`}>
      {value}
    </p>
    {subtext && <p className="text-white/30 text-[10px]">{subtext}</p>}
  </div>
);

// ==================== FEATURE ROW ====================
const FeatureRow = ({ feature, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  const Icon = FEATURE_ICONS[feature.id] || Activity;
  return (
    <div className="flex items-center gap-3" data-testid={`feature-row-${feature.id}`}>
      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
        <Icon size={12} style={{ color }} />
      </div>
      <span className="text-white/70 text-xs font-medium flex-1 truncate">{feature.label}</span>
      <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden flex-shrink-0">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-white/50 text-xs font-mono w-12 text-right flex-shrink-0">{count.toLocaleString()}</span>
    </div>
  );
};

// ==================== MAIN ANALYTICS COMPONENT ====================
const AnalyticsAdmin = (props) => {
  const { token, onAlertCount } = props;
  const [period, setPeriod] = useState('7d');
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tablesExist, setTablesExist] = useState(null);
  const [activeFeature, setActiveFeature] = useState(null);
  const [featureData, setFeatureData] = useState(null);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sponsorData, setSponsorData] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [alertHistory, setAlertHistory] = useState(null);
  const [alertHistoryLoading, setAlertHistoryLoading] = useState(false);

  const checkSetup = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/analytics/setup/status`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      setTablesExist(data.tables_exist);
      return data.tables_exist;
    } catch {
      setTablesExist(false);
      return false;
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = showCustom && customStart && customEnd
      ? `start_date=${customStart}T00:00:00Z&end_date=${customEnd}T23:59:59Z`
      : `period=${period}`;
    try {
      const [ovRes, revRes, spRes] = await Promise.all([
        fetch(`${API}/api/admin/analytics/overview?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/analytics/revenue?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/analytics/sponsors?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const [ovData, revData, spData] = await Promise.all([ovRes.json(), revRes.json(), spRes.json()]);
      setOverview(ovData);
      setRevenue(revData);
      setSponsorData(spData);
    } catch (e) {
      console.error('Analytics fetch error:', e);
    }
    // Fetch alerts independently — failure must never affect main data
    try {
      const alertRes = await fetch(`${API}/api/admin/analytics/alerts`, { headers: { Authorization: `Bearer ${token}` } });
      if (alertRes.ok) {
        const alertData = await alertRes.json();
        setAlerts(alertData);
        if (onAlertCount) onAlertCount(alertData.alert_count || 0);
      }
    } catch {}
    // Fetch alert history independently
    try {
      setAlertHistoryLoading(true);
      const ahRes = await fetch(`${API}/api/admin/analytics/alert-history?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (ahRes.ok) {
        const ahData = await ahRes.json();
        setAlertHistory(ahData);
      }
    } catch {} finally { setAlertHistoryLoading(false); }
    setLoading(false);
  }, [token, period, showCustom, customStart, customEnd]);

  useEffect(() => {
    checkSetup().then(exists => {
      if (exists) fetchData();
      else setLoading(false);
    });
  }, [checkSetup, fetchData]);

  const fetchFeature = useCallback(async (featureId) => {
    setFeatureLoading(true);
    const params = showCustom && customStart && customEnd
      ? `start_date=${customStart}T00:00:00Z&end_date=${customEnd}T23:59:59Z`
      : `period=${period}`;
    try {
      const r = await fetch(`${API}/api/admin/analytics/feature/${featureId}?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setFeatureData(await r.json());
    } catch { setFeatureData(null); }
    setFeatureLoading(false);
  }, [token, period, showCustom, customStart, customEnd]);

  useEffect(() => {
    if (activeFeature && tablesExist) fetchFeature(activeFeature);
  }, [activeFeature, fetchFeature, tablesExist]);

  const handleVerify = async () => {
    const exists = await checkSetup();
    if (exists) {
      toast.success('Analytics tables verified!');
      fetchData();
    } else {
      toast.error('Tables not found. Please run the SQL migration first.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = showCustom && customStart && customEnd
        ? `start_date=${customStart}T00:00:00Z&end_date=${customEnd}T23:59:59Z`
        : `period=${period}`;
      const r = await fetch(`${API}/api/admin/analytics/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const disposition = r.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `mediterranean_analytics_${period}.csv`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) {
      toast.error('Export failed. Please try again.');
    }
    setExporting(false);
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const params = showCustom && customStart && customEnd
        ? `start_date=${customStart}T00:00:00Z&end_date=${customEnd}T23:59:59Z`
        : `period=${period}`;
      const r = await fetch(`${API}/api/admin/analytics/export-pdf?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('PDF export failed');
      const blob = await r.blob();
      const disposition = r.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="(.+)"/);
      const filename = match ? match[1] : `mediterranean_report_${period}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (e) {
      toast.error('PDF export failed. Please try again.');
    }
    setExportingPdf(false);
  };

  // Loading state
  if (loading && tablesExist === null) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[#E91E8D]" />
      </div>
    );
  }

  // Setup required
  if (tablesExist === false) {
    return <SetupWizard token={token} onSetupComplete={handleVerify} />;
  }

  // Prepare chart data from daily_counts
  const chartData = Object.entries(overview?.daily_counts || {}).map(([date, count]) => ({
    date: date.slice(5), // MM-DD format
    events: count,
    revenue: (overview?.daily_revenue || {})[date] || 0,
  }));

  const totalFeatureEvents = Object.values(overview?.feature_counts || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-white font-bold text-base flex items-center gap-2">
            Analytics Portal
            {alerts && alerts.alert_count > 0 && (
              <span className="bg-[#E91E8D] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid="alert-badge">
                {alerts.alert_count}
              </span>
            )}
          </h2>
          <p className="text-white/30 text-xs mt-0.5">Mediterranean engagement & revenue insights</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportPdf}
            disabled={exportingPdf}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-500/10 text-purple-400 text-xs font-semibold border border-purple-500/20 hover:bg-purple-500/20 transition-colors disabled:opacity-40"
            data-testid="export-pdf-btn"
          >
            {exportingPdf ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            {exportingPdf ? 'Generating...' : 'Export PDF'}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E91E8D]/10 text-[#E91E8D] text-xs font-semibold border border-[#E91E8D]/20 hover:bg-[#E91E8D]/20 transition-colors disabled:opacity-40"
            data-testid="export-report-btn"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button onClick={() => fetchData()} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors" data-testid="refresh-analytics">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex flex-wrap items-center gap-2" data-testid="period-selector">
        {PERIODS.map(p => (
          <button
            key={p.id}
            onClick={() => { setPeriod(p.id); setShowCustom(false); }}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              period === p.id && !showCustom
                ? 'bg-[#E91E8D]/15 text-[#E91E8D] border border-[#E91E8D]/25'
                : 'bg-white/5 text-white/40 border border-transparent hover:text-white/60 hover:bg-white/10'
            }`}
            data-testid={`period-${p.id}`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setShowCustom(!showCustom)}
          className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
            showCustom
              ? 'bg-[#E91E8D]/15 text-[#E91E8D] border border-[#E91E8D]/25'
              : 'bg-white/5 text-white/40 border border-transparent hover:text-white/60 hover:bg-white/10'
          }`}
          data-testid="period-custom"
        >
          <Calendar size={12} />
          Custom
          <ChevronDown size={10} className={`transition-transform ${showCustom ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Custom date range */}
      {showCustom && (
        <div className="glass-card p-4 flex flex-col sm:flex-row items-start sm:items-end gap-3" data-testid="custom-date-range">
          <div>
            <label className="text-white/40 text-[10px] block mb-1">Start Date</label>
            <input
              type="date"
              value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="input-med !py-1.5 !text-xs"
              data-testid="custom-start-date"
            />
          </div>
          <div>
            <label className="text-white/40 text-[10px] block mb-1">End Date</label>
            <input
              type="date"
              value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="input-med !py-1.5 !text-xs"
              data-testid="custom-end-date"
            />
          </div>
          <button
            onClick={fetchData}
            disabled={!customStart || !customEnd}
            className="btn-pink !text-xs !py-1.5"
            data-testid="apply-custom-range"
          >
            Apply
          </button>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-[#E91E8D]" />
        </div>
      )}

      {/* KPI Cards */}
      {!loading && alerts && alerts.alert_count > 0 && (
        <div className="glass-card p-4" data-testid="engagement-alerts">
          <div className="flex items-center gap-2 mb-3">
            <Bell size={14} className="text-[#E91E8D]" />
            <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase">Engagement Alerts</h3>
            <span className="bg-[#E91E8D]/20 text-[#E91E8D] text-[9px] font-bold px-2 py-0.5 rounded-full">{alerts.alert_count}</span>
            <span className="text-white/15 text-[9px] ml-auto">vs 14-day avg</span>
          </div>
          <div className="space-y-1.5">
            {alerts.alerts.map((a, i) => <AlertRow key={i} alert={a} />)}
          </div>
        </div>
      )}

      {/* Alert History */}
      {!loading && (
        <div className="glass-card p-4" data-testid="alert-history-section">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} className="text-purple-400" />
            <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase">Alert History</h3>
            {alertHistory && alertHistory.total > 0 && (
              <span className="bg-purple-500/20 text-purple-400 text-[9px] font-bold px-2 py-0.5 rounded-full">{alertHistory.total}</span>
            )}
            <span className="text-white/15 text-[9px] ml-auto">Persisted KPI spikes</span>
          </div>
          {alertHistoryLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={16} className="animate-spin text-purple-400" />
            </div>
          ) : alertHistory && alertHistory.alerts && alertHistory.alerts.length > 0 ? (
            <div className="space-y-1" data-testid="alert-history-list">
              {alertHistory.alerts.map((ah, i) => (
                <AlertHistoryRow key={ah.dedup_key || i} alert={ah} />
              ))}
            </div>
          ) : (
            <p className="text-white/20 text-xs text-center py-6" data-testid="alert-history-empty">
              No engagement alert spikes recorded for this period.
            </p>
          )}
        </div>
      )}
      {!loading && overview && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-cards">
            <KPICard
              icon={Activity}
              label="Total Events"
              value={overview.total_events.toLocaleString()}
              color="#E91E8D"
              testId="kpi-total-events"
            />
            <KPICard
              icon={Users}
              label="Unique Users"
              value={overview.unique_users.toLocaleString()}
              color="#3B82F6"
              testId="kpi-unique-users"
            />
            <KPICard
              icon={DollarSign}
              label="Revenue"
              value={centsToUSD(overview.total_revenue_cents)}
              color="#10B981"
              testId="kpi-revenue"
            />
            <KPICard
              icon={TrendingUp}
              label="Transactions"
              value={(revenue?.transaction_count || 0).toLocaleString()}
              color="#F59E0B"
              testId="kpi-transactions"
            />
          </div>

          {/* Activity Chart */}
          {chartData.length > 0 && (
            <div className="glass-card p-5" data-testid="activity-chart">
              <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase mb-4">Event Activity</h3>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="eventGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#E91E8D" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#E91E8D" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} width={35} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="events" stroke="#E91E8D" fill="url(#eventGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Revenue Chart */}
          {Object.keys(overview?.daily_revenue || {}).length > 0 && (
            <div className="glass-card p-5" data-testid="revenue-chart">
              <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase mb-4">Daily Revenue</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 10 }} axisLine={false} tickLine={false} width={50}
                    tickFormatter={(v) => `$${(v / 100).toFixed(0)}`} />
                  <Tooltip content={<CustomTooltip isCurrency />} />
                  <Bar dataKey="revenue" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Feature Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glass-card p-5" data-testid="feature-breakdown">
              <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase mb-4">Feature Usage</h3>
              <div className="space-y-3">
                {FEATURES.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setActiveFeature(activeFeature === f.id ? null : f.id)}
                    className={`w-full text-left transition-all rounded-lg p-0.5 ${activeFeature === f.id ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}
                    data-testid={`feature-btn-${f.id}`}
                  >
                    <FeatureRow
                      feature={f}
                      count={overview.feature_counts[f.id] || 0}
                      total={totalFeatureEvents}
                      color={f.color}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Revenue by Feature */}
            <div className="glass-card p-5" data-testid="revenue-breakdown">
              <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase mb-4">Revenue by Feature</h3>
              {Object.keys(revenue?.by_feature || {}).length === 0 ? (
                <p className="text-white/20 text-xs text-center py-8">No revenue data yet</p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(revenue?.by_feature || {}).sort((a, b) => b[1].total_cents - a[1].total_cents).map(([feat, data]) => {
                    const featureInfo = FEATURES.find(f => f.id === feat) || { label: feat, color: '#888' };
                    const FeatureIcon = FEATURE_ICONS[feat] || DollarSign;
                    return (
                      <div key={feat} className="flex items-center gap-3" data-testid={`revenue-feature-${feat}`}>
                        <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${featureInfo.color}15` }}>
                          <FeatureIcon size={12} style={{ color: featureInfo.color }} />
                        </div>
                        <span className="text-white/70 text-xs font-medium flex-1 truncate">{featureInfo.label}</span>
                        <span className="text-white/40 text-[10px]">{data.count} txn{data.count !== 1 ? 's' : ''}</span>
                        <span className="text-green-400 text-xs font-mono font-bold">{centsToUSD(data.total_cents)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sponsor Analytics */}
          <div className="glass-card p-5" data-testid="sponsor-analytics">
            <div className="flex items-center gap-2 mb-4">
              <Megaphone size={14} className="text-amber-400" />
              <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase">Sponsor Analytics</h3>
            </div>

            {(!sponsorData || (sponsorData.total_impressions === 0 && sponsorData.total_clicks === 0)) ? (
              <p className="text-white/20 text-xs text-center py-6">
                No sponsor impression/click data in this period. Tracking begins automatically when sponsors are viewed.
              </p>
            ) : (
              <div className="space-y-4">
                {/* Sponsor KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {(() => {
                    const kpiData = [
                      { label: 'Impressions', value: (sponsorData?.total_impressions || 0).toLocaleString(), color: '#8B5CF6' },
                      { label: 'Unique Reach', value: (sponsorData?.unique_viewers || 0).toLocaleString(), color: '#3B82F6' },
                      { label: 'Clicks', value: (sponsorData?.total_clicks || 0).toLocaleString(), color: '#10B981' },
                      { label: 'Unique Clickers', value: (sponsorData?.unique_clickers || 0).toLocaleString(), color: '#06B6D4' },
                      { label: 'CTR', value: `${sponsorData?.ctr || 0}%`, color: '#F59E0B' },
                    ];
                    const kpiIcons = [Eye, Users, MousePointerClick, MousePointerClick, TrendingUp];
                    return kpiData.map((kpi, idx) => {
                      const KpiIcon = kpiIcons[idx];
                      return (
                        <div key={kpi.label} className="text-center p-2.5 rounded-lg bg-white/[0.03]" data-testid={`sponsor-kpi-${kpi.label.toLowerCase().replace(/ /g,'-')}`}>
                          <KpiIcon size={14} className="mx-auto mb-1.5" style={{ color: kpi.color }} />
                          <p className="text-white font-bold text-sm leading-none">{kpi.value}</p>
                          <p className="text-white/30 text-[9px] mt-1">{kpi.label}</p>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Per-sponsor performance table */}
                {sponsorData?.sponsors?.length > 0 && (
                  <div>
                    <p className="text-white/30 text-[10px] font-bold tracking-wider uppercase mb-2">Campaign Performance</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[10px]" data-testid="sponsor-performance-table">
                        <thead>
                          <tr className="border-b border-white/5">
                            <th className="text-left text-white/40 pb-2 pr-3 font-semibold">Sponsor</th>
                            <th className="text-right text-white/40 pb-2 px-2 font-semibold">Impressions</th>
                            <th className="text-right text-white/40 pb-2 px-2 font-semibold">Reach</th>
                            <th className="text-right text-white/40 pb-2 px-2 font-semibold">Clicks</th>
                            <th className="text-right text-white/40 pb-2 px-2 font-semibold">Clickers</th>
                            <th className="text-right text-white/40 pb-2 pl-2 font-semibold">CTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sponsorData.sponsors.map(sp => (
                            <tr key={sp.sponsor_id} className="border-b border-white/[0.03]">
                              <td className="py-2 pr-3 text-white/70 font-medium truncate max-w-[120px]">{sp.sponsor_name || sp.sponsor_id}</td>
                              <td className="py-2 px-2 text-right text-white/50 font-mono">{sp.impressions.toLocaleString()}</td>
                              <td className="py-2 px-2 text-right text-white/50 font-mono">{sp.unique_viewers.toLocaleString()}</td>
                              <td className="py-2 px-2 text-right text-white/50 font-mono">{sp.clicks.toLocaleString()}</td>
                              <td className="py-2 px-2 text-right text-white/50 font-mono">{sp.unique_clickers.toLocaleString()}</td>
                              <td className="py-2 pl-2 text-right text-amber-400 font-mono font-bold">{sp.ctr}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Sponsor daily trend */}
                {Object.keys(sponsorData?.daily_impressions || {}).length > 0 && (
                  <div>
                    <p className="text-white/30 text-[10px] font-bold tracking-wider uppercase mb-2">Impressions Over Time</p>
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart data={Object.entries(sponsorData.daily_impressions).map(([d, imp]) => ({
                        date: d.slice(5),
                        impressions: imp,
                        clicks: (sponsorData.daily_clicks || {})[d] || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} axisLine={false} tickLine={false} width={30} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="impressions" fill="#8B5CF6" radius={[3, 3, 0, 0]} name="Impressions" />
                        <Bar dataKey="clicks" fill="#10B981" radius={[3, 3, 0, 0]} name="Clicks" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Feature Deep Dive */}
          {activeFeature && featureData && (
            <div className="glass-card p-5" data-testid="feature-deep-dive">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase">
                  {FEATURES.find(f => f.id === activeFeature)?.label || activeFeature} — Deep Dive
                </h3>
                <button onClick={() => setActiveFeature(null)} className="text-white/30 hover:text-white/60 text-xs">
                  Close
                </button>
              </div>

              {featureLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={16} className="animate-spin text-white/30" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                      <p className="text-white font-bold text-sm">{featureData.total_events.toLocaleString()}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">Events</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                      <p className="text-white font-bold text-sm">{featureData.unique_users.toLocaleString()}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">Users</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                      <p className="text-green-400 font-bold text-sm">{centsToUSD(featureData.total_revenue_cents)}</p>
                      <p className="text-white/30 text-[10px] mt-0.5">Revenue</p>
                    </div>
                  </div>

                  {/* Event types */}
                  {Object.keys(featureData.event_types || {}).length > 0 && (
                    <div>
                      <p className="text-white/30 text-[10px] font-bold tracking-wider uppercase mb-2">Event Types</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(featureData.event_types).map(([type, count]) => (
                          <span key={type} className="px-2.5 py-1 rounded-md bg-white/5 text-white/50 text-[10px] font-mono">
                            {type}: <span className="text-white/80 font-bold">{count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Feature daily chart */}
                  {Object.keys(featureData.daily_counts || {}).length > 0 && (
                    <div>
                      <p className="text-white/30 text-[10px] font-bold tracking-wider uppercase mb-2">Daily Trend</p>
                      <ResponsiveContainer width="100%" height={120}>
                        <AreaChart data={Object.entries(featureData.daily_counts).map(([d, c]) => ({ date: d.slice(5), events: c }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                          <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 9 }} axisLine={false} tickLine={false} width={30} />
                          <Area type="monotone" dataKey="events" stroke={FEATURES.find(f => f.id === activeFeature)?.color || '#E91E8D'} fill={`${FEATURES.find(f => f.id === activeFeature)?.color || '#E91E8D'}20`} strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Recent events */}
                  {featureData.recent_events?.length > 0 && (
                    <div>
                      <p className="text-white/30 text-[10px] font-bold tracking-wider uppercase mb-2">Recent Events</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {featureData.recent_events.map((evt, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px] py-1 border-b border-white/[0.03]">
                            <span className="text-white/30 font-mono">{new Date(evt.created_at).toLocaleString()}</span>
                            <span className="text-white/50">{evt.event_type}</span>
                            {evt.amount_cents > 0 && <span className="text-green-400 font-bold ml-auto">{centsToUSD(evt.amount_cents)}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Event Type Distribution */}
          {Object.keys(overview?.event_type_counts || {}).length > 0 && (
            <div className="glass-card p-5" data-testid="event-type-distribution">
              <h3 className="text-white/60 text-[10px] font-bold tracking-widest uppercase mb-4">Event Type Distribution</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(overview.event_type_counts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <span key={type} className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/60 text-[10px] font-mono border border-white/[0.04]">
                      {type} <span className="text-[#E91E8D] font-bold ml-1">{count.toLocaleString()}</span>
                    </span>
                  ))
                }
              </div>
            </div>
          )}

          {/* Empty state */}
          {overview?.total_events === 0 && (
            <div className="glass-card p-8 text-center" data-testid="analytics-empty-state">
              <Activity size={32} className="text-white/10 mx-auto mb-3" />
              <p className="text-white/30 text-sm font-medium mb-1">No analytics data yet</p>
              <p className="text-white/15 text-xs">
                Events will appear here as users interact with Mediterranean features.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsAdmin;
